import {
    normalizeWidgetTheme,
    APIURL,
    cancelSelectEvent,
    changeSymbolForWidget,
    getCSSStyles,
    getSymbolDropdown,
    getTraderSMCardHTML,
    getWidgetDataURI,
    apiGet,
    toggleWidgetTheme,
    getEChartsTooltipTheme,
    getHeaderPinStateClass,
    getHeaderPinButtonHTML,
    setupHeaderPin
} from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';

interface MarimekkoPeriod {
    period_label: string;
    year: number;
    quarter: string;
    total_revenue: number;
    total_revenue_billions: number;
    display_total: string;
}

interface MarimekkoSegment {
    category: string;
    period_label: string;
    year: number;
    quarter: string;
    revenue: number;
    revenue_billions: number;
    percentage: number;
    total_revenue: number;
    display_value: string;
    display_percentage: string;
}

interface MarimekkoSummary {
    total_revenue_all_periods: number;
    avg_revenue_per_period: number;
    category_count: number;
    period_count: number;
}

interface MarimekkoApiData {
    symbol: string;
    country: string;
    start_year: number;
    end_year: number;
    total_periods: number;
    categories: string[];
    periods: MarimekkoPeriod[];
    segments: MarimekkoSegment[];
    summary?: MarimekkoSummary;
}

interface MarimekkoBlock {
    category: string;
    period_label: string;
    x0: number;
    x1: number;
    y0: number;
    y1: number;
    revenue: number;
    percentage: number;
    display_value: string;
    display_percentage: string;
    display_total: string;
    color: string;
}

interface RevenueProductSegmentsMarimekkoConfig extends WidgetConfig {
    startYear?: number;
    endYear?: number;
}

const SEGMENT_COLORS = ['#7a00ff', '#4c00d6', '#33D17A', '#FFB020', '#FF6B6B', '#00B4D8', '#9B59B6', '#E67E22'];

export class TraderverseRevenueProductSegmentsMarrimekkoChart {
    private container: HTMLElement;
    private config: RevenueProductSegmentsMarimekkoConfig;
    private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
    private shadowRoot: ShadowRoot;
    private apiData: MarimekkoApiData | null = null;
    private chartBlocks: MarimekkoBlock[] = [];
    private segmentLookup: Map<string, MarimekkoSegment> = new Map();
    private categoryColors: Map<string, string> = new Map();
    private hiddenCategories: Set<string> = new Set();
    private currentChart: any;
    private widgetResizeObserver: ResizeObserver | null = null;
    private outsideClickHandler: ((ev: MouseEvent) => void) | null = null;
    private filters = {
        startYear: new Date().getFullYear() - 5,
        endYear: new Date().getFullYear()
    };

    constructor(config: RevenueProductSegmentsMarimekkoConfig) {
        this.config = { ...this.getDefaultConfig(), ...config };
        this.config.theme = normalizeWidgetTheme(this.config.theme);
        this.applyConfigFilters();
        this.container = this.resolveContainer(config.container);

        if ((this.container as any).shadowRoot) {
            this.shadowRoot = (this.container as any).shadowRoot;
        } else {
            this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
        }
        this.init();

        this.widgetResizeObserver = new ResizeObserver(() => {
            this.updateDisplay();
        });
        setTimeout(() => {
            const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
            if (widget) this.widgetResizeObserver?.observe(widget);
        }, 0);
    }

    private fetchSymbols(): Array<{ value: string; label: string }> {
        return [
            { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
            { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
            { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
            { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
            { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' }
        ];
    }

    private getDefaultConfig(): Partial<RevenueProductSegmentsMarimekkoConfig> {
        const symbols = this.fetchSymbols();
        const currentYear = new Date().getFullYear();
        return {
            headerPinned: true,
            symbol: symbols[0].value,
            theme: 'default-dark',
            width: '100%',
            height: '400px',
            autosize: true,
            isSymbolSelectable: true,
            startYear: currentYear - 5,
            endYear: currentYear
        };
    }

    private applyConfigFilters(): void {
        if (this.config.startYear) this.filters.startYear = this.config.startYear;
        if (this.config.endYear) this.filters.endYear = this.config.endYear;
        if (this.filters.startYear > this.filters.endYear) {
            this.filters.endYear = this.filters.startYear;
        }
    }

    private getYearOptions(): number[] {
        const currentYear = new Date().getFullYear();
        return Array.from({ length: 15 }, (_, i) => currentYear - i);
    }

    private resolveContainer(container: string | HTMLElement): HTMLElement {
        if (typeof container === 'string') {
            const el = document.getElementById(container) || document.querySelector(container);
            if (!el) throw new Error(`Container not found: ${container}`);
            return el as HTMLElement;
        }
        return container;
    }

    private filtersIconSvg(): string {
        return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>`;
    }

    private renderYearOptions(selectedYear: number): string {
        return this.getYearOptions()
            .map(year => `<option value="${year}"${year === selectedYear ? ' selected' : ''}>${year}</option>`)
            .join('');
    }

    private async init(): Promise<void> {
        if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;
        this.createWidget();
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        if (widget) widget.classList.add('loading');
        await this.injectChartLibrary();
        await this.attachStyles();
        this.setupEventListeners();
        await this.fetchData(this.config.symbol!);
        if (widget) widget.classList.remove('loading');
        this.emit('ready', {});
        cancelSelectEvent(this.shadowRoot);
        this.config.onReady?.();
    }

    private createWidget(): void {
        let themeClass = 'defaultDarkMode';
        let dataTheme = 'default-dark';

        if (this.config.theme === 'white') {
            themeClass = 'whiteMode';
            dataTheme = 'white';
        } else if (this.config.theme === 'black') {
            themeClass = 'blackMode';
            dataTheme = 'black';
        } else if (this.config.theme === 'default-lite') {
            themeClass = 'defaultLightMode';
            dataTheme = 'default-lite';
        }

        const widgetHtml = `
  <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title stock">${this.getTitleIconSvg()} Revenue Product Segments Marimekko</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
              <div class="item">
                <button class="filters-toggle" type="button" aria-label="Toggle filters">${this.filtersIconSvg()}</button>
              </div>
              <i class="pi pi-ellipsis-v"></i>
            </div>
          </div>
          <div class="inBody">
            <div class="optionsRow" data-testing-id="options-row">
                ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                    <div class="trader-sm-card" id="selected-symbol">
                        ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: 'stock', name: '' })}
                    </div>
                    <span id="symbol-dropdown-placeholder"></span>
                </div>`}
                <span></span>
                <div class="filtersBox sideBar hidden">
                  <div class="item">
                    <label>Start Year</label>
                    <select class="start-year-select">${this.renderYearOptions(this.filters.startYear)}</select>
                  </div>
                  <div class="item">
                    <label>End Year</label>
                    <select class="end-year-select">${this.renderYearOptions(this.filters.endYear)}</select>
                  </div>
                </div>
                <div class="item">
                    <div class="legendsCollection" id="custom-legends"></div>
                </div>
            </div>
            <div class="chartDv cryptoCompany" data-testing-id="chart-container">
                <div id="revenueProductSegmentsMarimekkoContainer"></div>
            </div>
          </div>
        </div>
    `;
        this.shadowRoot.innerHTML = widgetHtml;
        setupHeaderPin(this.shadowRoot, this.config);

        if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
            const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
            const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
            if (placeholder && dropdown) placeholder.replaceWith(dropdown);
        }

        const filterBtn = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;
        if (filterBtn && filtersBox) {
            filterBtn.addEventListener('click', (ev: MouseEvent) => {
                ev.stopPropagation();
                filtersBox.classList.toggle('hidden');
            });
        }
    }

    onSelectChange(stock: any) {
        this.changeSymbol(stock.symbol, stock);
    }

    private setupEventListeners(): void {
        const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement | null;

        if (filtersBox) {
            filtersBox.addEventListener('click', (ev: MouseEvent) => ev.stopPropagation());
            this.outsideClickHandler = (ev: MouseEvent) => {
                const anyEv: any = ev;
                const path: EventTarget[] = (anyEv.composedPath && anyEv.composedPath()) || anyEv.path || [];
                const clickedInside = path.indexOf(this.container) !== -1 || (widgetBox && path.indexOf(widgetBox) !== -1);
                if (!clickedInside && !filtersBox.classList.contains('hidden')) {
                    filtersBox.classList.add('hidden');
                }
            };
            document.addEventListener('click', this.outsideClickHandler);
        }

        const startYearSelect = this.shadowRoot.querySelector('.start-year-select') as HTMLSelectElement;
        const endYearSelect = this.shadowRoot.querySelector('.end-year-select') as HTMLSelectElement;

        const applyFiltersAndFetch = () => {
            this.filters.startYear = Number(startYearSelect?.value || this.filters.startYear);
            this.filters.endYear = Number(endYearSelect?.value || this.filters.endYear);
            if (this.filters.startYear > this.filters.endYear) {
                this.filters.endYear = this.filters.startYear;
                if (endYearSelect) endYearSelect.value = String(this.filters.endYear);
            }
            this.config.startYear = this.filters.startYear;
            this.config.endYear = this.filters.endYear;
            this.fetchData(this.config.symbol!);
        };

        startYearSelect?.addEventListener('change', applyFiltersAndFetch);
        endYearSelect?.addEventListener('change', applyFiltersAndFetch);

        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        dropdown?.addEventListener('change', e => {
            const value = (e.target as HTMLSelectElement).value;
            this.changeSymbol(value);
        });
        if (dropdown && this.config.symbol) dropdown.value = this.config.symbol;
    }

    private async attachStyles(): Promise<void> {
        try {
            const styleEl = await getCSSStyles();
            this.shadowRoot.appendChild(styleEl);
        } catch (err) {
            console.error('Failed to load CSS file:', err);
        }
    }

    private async injectChartLibrary(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if ((window as any).echarts) return resolve(true);
            if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
                const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
                script.onload = () => resolve(true);
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
                script.async = true;
                script.setAttribute('data-chart-lib', 'true');
                this.shadowRoot.appendChild(script);
                script.onload = () => resolve(true);
            }
        });
    }

    private extractApiData(raw: any): MarimekkoApiData | null {
        if (!raw) return null;
        if (Array.isArray(raw.periods) && Array.isArray(raw.segments)) return raw as MarimekkoApiData;
        if (raw.data && Array.isArray(raw.data.periods) && Array.isArray(raw.data.segments)) {
            return raw.data as MarimekkoApiData;
        }
        return null;
    }

    private buildSegmentLookup(data: MarimekkoApiData): void {
        this.segmentLookup.clear();
        (data.segments || []).forEach(segment => {
            this.segmentLookup.set(`${segment.period_label}::${segment.category}`, segment);
        });
    }

    private buildCategoryColors(categories: string[]): void {
        this.categoryColors.clear();
        categories.forEach((category, index) => {
            this.categoryColors.set(category, SEGMENT_COLORS[index % SEGMENT_COLORS.length]);
        });
    }

    private getSegment(periodLabel: string, category: string): MarimekkoSegment | undefined {
        return this.segmentLookup.get(`${periodLabel}::${category}`);
    }

    private bindApiResponse(raw: any): boolean {
        const data = this.extractApiData(raw);
        if (!data?.periods?.length || !data?.segments?.length) {
            this.apiData = null;
            this.chartBlocks = [];
            this.segmentLookup.clear();
            this.categoryColors.clear();
            return false;
        }

        this.apiData = data;
        this.buildSegmentLookup(data);
        this.buildCategoryColors(data.categories || []);
        this.chartBlocks = this.buildChartBlocks(data);
        return this.chartBlocks.length > 0;
    }

    private buildChartBlocks(
        data: MarimekkoApiData,
        categories: string[] = data.categories || [],
        normalizeHeights = false
    ): MarimekkoBlock[] {
        const periods = data.periods || [];
        if (!periods.length || !categories.length) return [];

        const totalRevenue = periods.reduce((sum, period) => sum + (period.total_revenue || 0), 0);
        if (!totalRevenue) return [];

        const blocks: MarimekkoBlock[] = [];
        let xOffset = 0;

        periods.forEach(period => {
            const periodWidth = ((period.total_revenue || 0) / totalRevenue) * 100;
            let yOffset = 0;

            const visibleTotal = normalizeHeights
                ? categories.reduce((sum, category) => sum + (this.getSegment(period.period_label, category)?.percentage || 0), 0)
                : 100;

            categories.forEach(category => {
                const segment = this.getSegment(period.period_label, category);
                if (!segment?.percentage) return;

                const height = normalizeHeights && visibleTotal
                    ? (segment.percentage / visibleTotal) * 100
                    : segment.percentage;

                blocks.push({
                    category,
                    period_label: period.period_label,
                    x0: xOffset,
                    x1: xOffset + periodWidth,
                    y0: yOffset,
                    y1: yOffset + height,
                    revenue: segment.revenue,
                    percentage: segment.percentage,
                    display_value: segment.display_value,
                    display_percentage: segment.display_percentage,
                    display_total: period.display_total,
                    color: this.categoryColors.get(category) || SEGMENT_COLORS[0]
                });
                yOffset += height;
            });

            xOffset += periodWidth;
        });

        return blocks;
    }

    private getVisibleBlocks(): MarimekkoBlock[] {
        if (!this.apiData) return [];
        if (!this.hiddenCategories.size) return this.chartBlocks;

        const visibleCategories = (this.apiData.categories || []).filter(
            category => !this.hiddenCategories.has(category)
        );
        return this.buildChartBlocks(this.apiData, visibleCategories, true);
    }

    private async fetchData(symbol: string): Promise<void> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const container = this.shadowRoot.querySelector('#revenueProductSegmentsMarimekkoContainer');
        if (widget) widget.classList.add('loading');

        try {
            if ((this.config as any).data) {
                if (!this.bindApiResponse((this.config as any).data)) {
                    throw new Error('Invalid revenue product segments marimekko data');
                }
                this.updateDisplay();
                this.emit('dataUpdate', this.apiData);
                return;
            }

            const response = await apiGet({
                method: 'GET',
                url: `${APIURL}/v1/symbol-specific/revenue-product-segments-merrimekko`,
                query: {
                    symbol,
                    startYear: this.filters.startYear,
                    endYear: this.filters.endYear
                },
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error(`API error: ${response.status}`);
            const json = await response.json();
            if (!json?.isSuccess) throw new Error(json?.message || 'Failed to fetch revenue product segments marimekko');

            if (!this.bindApiResponse(json)) {
                throw new Error('No revenue product segments marimekko data available');
            }

            this.updateDisplay();
            this.emit('dataUpdate', {
                symbol,
                startYear: this.filters.startYear,
                endYear: this.filters.endYear,
                data: this.apiData
            });
        } catch (err) {
            console.error('Error fetching chart data:', err);
            this.apiData = null;
            this.chartBlocks = [];
            this.segmentLookup.clear();
            this.categoryColors.clear();
            if (container) container.innerHTML = '<div class="noData"><p>Failed to load data</p></div>';
            this.renderCustomLegends();
            this.emit('error', err);
        } finally {
            if (widget) widget.classList.remove('loading');
        }
    }

    private updateDisplay(): void {
        const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;
        if (this.chartBlocks?.length) {
            chartDv?.setAttribute('data-testing-id', 'revenue-product-segments-marimekko-chartdv');
        } else {
            chartDv?.removeAttribute('data-testing-id');
        }

        if (!(window as any).echarts) {
            setTimeout(() => this.updateDisplay(), 100);
            return;
        }

        if (!this.chartBlocks?.length) {
            const container = this.shadowRoot.querySelector('#revenueProductSegmentsMarimekkoContainer');
            if (container) container.innerHTML = '<div class="noData"><p>No revenue product segment data available.</p></div>';
            if (this.currentChart) {
                try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
                this.currentChart = null;
            }
            this.renderCustomLegends();
            return;
        }

        const chartEl = this.shadowRoot.querySelector('#revenueProductSegmentsMarimekkoChart') as HTMLElement | null;
        if (this.currentChart && chartEl) {
            this.drawChart();
            return;
        }

        this.renderChart();
    }

    private renderChart(): void {
        const container = this.shadowRoot.querySelector('#revenueProductSegmentsMarimekkoContainer');
        if (!container) return;

        container.innerHTML = `<div id="revenueProductSegmentsMarimekkoChart" class="chart coverDv" data-testing-id="revenue-product-segments-marimekko-chart"></div>`;
        this.drawChart();
    }

    private getInitialZoomEnd(periodCount: number): number {
        if (periodCount <= 8) return 100;
        return Math.min(100, Math.round((8 / periodCount) * 100));
    }

    private drawChart(): void {
        const echarts = (window as any).echarts;
        const container = this.shadowRoot.querySelector('#revenueProductSegmentsMarimekkoChart') as HTMLElement;
        if (!container || !this.chartBlocks?.length || !this.apiData) return;

        const visibleBlocks = this.getVisibleBlocks();
        if (!visibleBlocks.length) return;

        const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color').trim() || '#736D9D';
        const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm').trim() || '12px';
        const borderColor = getComputedStyle(widgetBox).getPropertyValue('--base-dark').trim() || '#1a1a2e';
        const periodCount = this.apiData.periods.length;
        const zoomEnd = this.getInitialZoomEnd(periodCount);

        if (!this.currentChart) {
            this.currentChart = echarts.init(container);
        }

        const option = {
            backgroundColor: 'transparent',
            tooltip: {
                trigger: 'item',
                ...getEChartsTooltipTheme(widgetBox),
                formatter: (params: any) => {
                    const block = visibleBlocks[params.dataIndex] as MarimekkoBlock | undefined;
                    if (!block) return '';
                    return `
                      <div style="min-width:180px">
                        <div style="font-weight:600;margin-bottom:6px">${block.period_label}</div>
                        <div style="margin-bottom:4px">
                          <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${block.color};margin-right:6px"></span>
                          ${block.category}
                        </div>
                        <div>Revenue <b>${block.display_value}</b></div>
                        <div>Share <b>${block.display_percentage}</b></div>
                        <div>Period Total <b>${block.display_total}</b></div>
                      </div>
                    `;
                }
            },
            grid: { left: 44, right: 12, top: 16, bottom: periodCount > 8 ? 72 : 56, containLabel: false },
            dataZoom: periodCount > 8 ? [
                {
                    type: 'slider',
                    xAxisIndex: 0,
                    filterMode: 'none',
                    height: 18,
                    bottom: 6,
                    start: 0,
                    end: zoomEnd,
                    borderColor: 'transparent',
                    backgroundColor: 'transparent',
                    fillerColor: 'rgba(122, 0, 255, 0.15)',
                    handleSize: 12
                },
                {
                    type: 'inside',
                    xAxisIndex: 0,
                    filterMode: 'none',
                    start: 0,
                    end: zoomEnd
                }
            ] : [],
            xAxis: {
                type: 'value',
                min: 0,
                max: 100,
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: false },
                axisLabel: { show: false }
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: 100,
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: {
                    show: true,
                    lineStyle: {
                        color: getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color')
                    }
                },
                axisLabel: {
                    color: textColor,
                    fontSize,
                    formatter: (value: number) => `${value}%`
                }
            },
            series: [
                {
                    type: 'custom',
                    coordinateSystem: 'cartesian2d',
                    renderItem: (params: any, api: any) => {
                        const block = visibleBlocks[params.dataIndex] as MarimekkoBlock | undefined;
                        if (!block) return;

                        const start = api.coord([block.x0, block.y0]);
                        const end = api.coord([block.x1, block.y1]);
                        const width = Math.max(end[0] - start[0], 0);
                        const height = Math.max(start[1] - end[1], 0);
                        const children: any[] = [
                            {
                                type: 'rect',
                                shape: {
                                    x: start[0],
                                    y: end[1],
                                    width,
                                    height
                                },
                                style: {
                                    fill: block.color,
                                    stroke: borderColor,
                                    lineWidth: 1
                                }
                            }
                        ];

                        if (height > 16 && width > 24) {
                            children.push({
                                type: 'text',
                                style: {
                                    text: block.display_percentage,
                                    x: start[0] + width / 2,
                                    y: end[1] + height / 2,
                                    fill: '#FFFFFF',
                                    fontSize: 10,
                                    textAlign: 'center',
                                    textVerticalAlign: 'middle'
                                }
                            });
                        }

                        if (block.y0 === 0) {
                            const centerX = start[0] + width / 2;
                            const baseY = api.coord([block.x0, 0])[1];
                            children.push({
                                type: 'text',
                                style: {
                                    text: `${block.period_label}\n${block.display_total}`,
                                    x: centerX,
                                    y: baseY + 18,
                                    fill: textColor,
                                    fontSize: 10,
                                    textAlign: 'center',
                                    textVerticalAlign: 'top'
                                }
                            });
                        }

                        return { type: 'group', children };
                    },
                    data: visibleBlocks
                }
            ]
        };

        this.currentChart.setOption(option, true);
        this.currentChart.resize();
        this.renderCustomLegends();
    }

    private renderCustomLegends(): void {
        const container = this.shadowRoot.querySelector('#custom-legends') as HTMLElement;
        if (!container) return;

        container.innerHTML = '';
        const categories = this.apiData?.categories || [];
        if (!categories.length) return;

        categories.forEach((category) => {
            const el = document.createElement('span');
            const isActive = !this.hiddenCategories.has(category);
            el.className = `toggle-series${isActive ? ' active' : ''}`;
            el.setAttribute('data-symbol', category);
            el.style.backgroundColor = this.categoryColors.get(category) || SEGMENT_COLORS[0];
            el.title = category;

            el.addEventListener('click', () => {
                if (this.hiddenCategories.has(category)) {
                    this.hiddenCategories.delete(category);
                } else {
                    this.hiddenCategories.add(category);
                }
                this.drawChart();
            });

            container.appendChild(el);
        });
    }

    private changeSymbol(stockId: string, stock: any = null): void {
        changeSymbolForWidget(
            this.shadowRoot,
            this.config,
            stock,
            stockId,
            (id: string) => this.fetchData(id),
            (type: WidgetEventType, data: any) => this.emit(type, data)
        );
    }

    public updateWithResponse(resp: any): void {
        if (!this.bindApiResponse(resp)) return;
        this.updateDisplay();
        this.emit('dataUpdate', this.apiData);
    }

    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
    }

    public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
    }

    public setSymbol(symbol: string): void {
        this.changeSymbol(symbol);
        const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
        if (dropdown) dropdown.value = symbol;
    }

    public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
    }

    public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite'): void {
        if (!this.shadowRoot) return;
        this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite';
        this.updateDisplay();
    }

    public destroy(): void {
        if (this.widgetResizeObserver) {
            this.widgetResizeObserver.disconnect();
            this.widgetResizeObserver = null;
        }
        if (this.outsideClickHandler) {
            document.removeEventListener('click', this.outsideClickHandler);
            this.outsideClickHandler = null;
        }
        if (this.currentChart) {
            try { this.currentChart.dispose(); } catch (e) { /* ignore */ }
            this.currentChart = null;
        }
        this.eventHandlers.clear();
        this.shadowRoot.innerHTML = '';
    }

    private emit(type: WidgetEventType, data: any): void {
        const handlers = this.eventHandlers.get(type);
        if (handlers) {
            const evt: WidgetEvent = { type, data };
            handlers.forEach(h => h(evt));
        }
    }

    private getTitleIconSvg(): string {
        return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
    }
}

declare global { interface Window { TraderverseRevenueProductSegmentsMarrimekkoChart: typeof TraderverseRevenueProductSegmentsMarrimekkoChart; } }
if (typeof window !== 'undefined') { window.TraderverseRevenueProductSegmentsMarrimekkoChart = TraderverseRevenueProductSegmentsMarrimekkoChart; }
